import {ANY_REACTION_COMPONENT_ROLE_LABEL, ChemistryPreferences} from 'chemistry/ChemistryConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ListingRendererType, ListingTypes} from 'nav/expanded/ListingRendererConstants';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import preferences from 'Preferences';
import * as FilterUtil from 'search/FilterUtil';
import {
  ChemicalStructureFilter,
  ChemicalStructureFilterFormat,
  ChemicalStructureSearchOperator,
  ChemicalStructureSearchParams,
  FilterKey,
  isChemicalStructureFilter,
  MultiValueFilter,
  Operator,
  SearchParams,
  SearchResults,
} from 'search/SearchConstants';
import {mergeFiltersToSearchParams} from 'search/SearchUtil';
import APIUtil from 'util/APIUtil';
import {MakeFieldsRequired} from 'util/Types';

import {ChemSearchFolderItemListingExtras} from './ChemistryConstants';

/**
 * Get the unique mol query from the search params, if it exists, otherwise null.
 */
export function extractMolQuery(searchParams: SearchParams): string | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter) && filter.format === ChemicalStructureFilterFormat.MOL) {
      return filter.value;
    }
  }
  return null;
}

export type CompleteChemSearchParams = MakeFieldsRequired<
  ChemicalStructureSearchParams,
  'chemicalStructureQuery'
>;

export function isCompleteChemSearchParams(
  chemSearchParams: ChemicalStructureSearchParams
): chemSearchParams is CompleteChemSearchParams {
  return chemSearchParams.chemicalStructureQuery != null;
}

export function isReactionChemSearch(searchParams: SearchParams): boolean {
  const existingTypesFilter = FilterUtil.getFilterByKey(
    searchParams.filters,
    FilterKey.TYPES
  ) as MultiValueFilter | null;
  return (
    existingTypesFilter?.value.length === 1 && existingTypesFilter.value[0] === ModelTypeConstants.REACTION
  );
}

export function getDefaultReactionComponentRole(searchParams: SearchParams): ChemistryReactionRole | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter) && filter.reactionComponentRole != null) {
      return parseChemistryReactionRole(filter.reactionComponentRole);
    }
  }
  return null;
}

/**
 * Return an updated copy of the given search params that performs the given chem search.
 */
export function updateChemSearchParams(
  searchParams: SearchParams,
  chemSearchParams: CompleteChemSearchParams
): SearchParams {
  const {chemicalStructureQuery, chemSearchOperator, minSimilarity, reactionComponentRole} = chemSearchParams;
  const isReactionOnly = isReactionChemSearch(searchParams);
  const chemicalStructureFilter: ChemicalStructureFilter = {
    key: FilterKey.CHEMICAL_STRUCTURE,
    operator: chemSearchOperator,
    format: ChemicalStructureFilterFormat.MOL,
    value: chemicalStructureQuery,
    minSimilarity,
  };
  if (isReactionOnly) {
    chemicalStructureFilter.reactionComponentRole = reactionComponentRole ?? null;
  }
  return mergeFiltersToSearchParams(searchParams, [
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: isReactionOnly ? [ModelTypeConstants.REACTION] : [ModelTypeConstants.MOLECULE],
    },
    chemicalStructureFilter,
  ]);
}
export interface ToggleHighlightParams {
  showToggleHighlightOption: boolean;
  shouldHighlightSubstructure: boolean;
  setShouldHighlightSubstructure: (shouldHighlightSubstructure: boolean) => void;
}

export function getChemSearchOperator(searchParams: SearchParams): Operator | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter)) {
      return filter.operator;
    }
  }
  return null;
}

export function isSubstructureSearch(searchParams: SearchParams): boolean {
  return getChemSearchOperator(searchParams) === Operator.SUBSTRUCTURE;
}

export function shouldShowChemSearchColumnsFirst(
  items: Array<ListingTypes[ListingRendererType.FOLDER_ITEM]>
): boolean {
  return items.every((item) => APIUtil.isMolecule(item.id));
}

export function isSimilaritySearch(searchParams: SearchParams): boolean {
  return getChemSearchOperator(searchParams) === Operator.SIMILARITY;
}

export function isChemSearch(searchParams: SearchParams): boolean {
  return isSubstructureSearch(searchParams) || isSimilaritySearch(searchParams);
}

export function getChemSearchFolderItemListingExtras(
  searchParams: SearchParams,
  chemicalStructureQuery: string | null,
  shouldHighlightSubstructure: boolean,
  searchResults: SearchResults | null
): ChemSearchFolderItemListingExtras | null {
  return isChemSearch(searchParams)
    ? {
        substructureQuery: isSubstructureSearch(searchParams) ? chemicalStructureQuery : null,
        shouldHighlightSubstructure,
        searchScoresById: searchResults?.scoresById,
      }
    : null;
}

export function getSimilarityOptionLabel(minSimilarity: number): string {
  return `>=${minSimilarity * 100}%`;
}

/** Roles shown in chem search UI; matches stoichiometry table. */
export const REACTION_COMPONENT_ROLE_UI_ORDER: Array<ChemistryReactionRole> = Object.values(
  ChemistryReactionRole
).sort((a, b) => CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[a] - CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[b]);

const UI_REACTION_COMPONENT_ROLES = new Set<string>(Object.values(ChemistryReactionRole));

function parseChemistryReactionRole(role: string): ChemistryReactionRole | null {
  if (UI_REACTION_COMPONENT_ROLES.has(role)) {
    return role as ChemistryReactionRole;
  }
  return null;
}

export function getReactionComponentRoleOptionLabel(role: ChemistryReactionRole | null): string {
  if (role == null) {
    return ANY_REACTION_COMPONENT_ROLE_LABEL;
  }
  return CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role];
}

export function getDefaultChemSearchOperator(searchParams: SearchParams): ChemicalStructureSearchOperator {
  if (isSimilaritySearch(searchParams)) {
    return Operator.SIMILARITY;
  } else if (isSubstructureSearch(searchParams)) {
    return Operator.SUBSTRUCTURE;
  } else {
    return preferences.get(ChemistryPreferences.CHEM_SEARCH_TYPE) ?? Operator.SUBSTRUCTURE;
  }
}

export function getChemSearchChipLabel(searchParams: SearchParams): string {
  let chipText = '';
  if (isSubstructureSearch(searchParams)) {
    chipText = 'Substructure Search';
  }
  if (isSimilaritySearch(searchParams)) {
    chipText = 'Similarity Search';
  }
  return chipText;
}
